import React, { useEffect, useState, MouseEventHandler, useCallback, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import { MsgType, IContent } from 'matrix-js-sdk';

import {
  Box,
  PopOut,
  Button,
  IconButton,
  RectCords,
  Text,
} from 'folds';
import Checkbox from '../../atoms/button/Checkbox';
import { useTypingStatusUpdater } from '../../hooks/useTypingStatusUpdater';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useAtom } from 'jotai';
import { aiAgentsAtom } from '../../utils/AICache';
import { CurveMakeUIConfigCmd, actStrLeaveRoom } from '../../utils/CurveCmds';
import { fetchAIAgents } from '../../../util/matrixUtil';
import { fetchUsersByRoles } from './CurveWork';
// eslint-disable-next-line import/no-extraneous-dependencies
// import "bootstrap-icons/font/bootstrap-icons.css";

import robotLogo from '../../../../public/icon/新增機器人按鈕.png';
import personLogo from '../../../../public/icon/新增真人按鈕.png';

import * as css from './styles.css';

type InvitedSelectionProps = { sroomid: string | undefined };
export function InvitedSelectionAI({ sroomid }: InvitedSelectionProps) {

  const mx = useMatrixClient();
  const [aiAgents, setAiAgents] = useAtom(aiAgentsAtom);
  const [menuAnchor, setMenuAnchor] = useState<RectCords>();
  const [selectedRobots, setSelectedRobots] = useState<{ [userId: string]: boolean }>({});
  const initialSelectedRobots = useRef<{ [userId: string]: boolean }>({});

  useEffect(() => {
    if (aiAgents.length === 0) {
      fetchAIAgents(mx).then(setAiAgents);
    }
  }, [mx, aiAgents.length, setAiAgents]);

  const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    // Re-sync initial state when opening menu
    const room = mx.getRoom(sroomid);
    const currentSelected: { [userId: string]: boolean } = {};
    if (room) {
      aiAgents.forEach(agent => {
        const userId = `@${agent.UserName}:CurveServer.net`;
        if (room.currentState.members.hasOwnProperty(userId)) {
          const { members } = room.currentState;
          if (members[userId].membership === 'join') currentSelected[userId] = true;
          else currentSelected[userId] = false;
        } else {
          currentSelected[userId] = false;
        }
      });
    }
    setSelectedRobots(currentSelected);
    initialSelectedRobots.current = { ...currentSelected };
    
    setMenuAnchor(evt.currentTarget.getBoundingClientRect());
  };


  const sendAILeaveRoomCmd = useCallback((userId: string) => {
    if (sroomid) {
      const room = mx.getRoom(sroomid);
      const plainText = CurveMakeUIConfigCmd(JSON.stringify({ cmd: actStrLeaveRoom, roomid: sroomid, userid: userId }));
      const msgType = MsgType.Text;

      const content: IContent = {
        msgtype: msgType,
        body: plainText,
      };
      if (room) {
        // @ts-ignore 型別不合但實際可用
        mx.sendMessage(room.roomId, content);
        // sendTypingStatus(false);
      }
    }
  }, [mx, sroomid]);

  const handleAddToRoom = async () => {
    if (sroomid) {
      for (const agent of aiAgents) {
        const userId = `@${agent.UserName}:CurveServer.net`;
        // Compare with initial state
        if (selectedRobots[userId] !== initialSelectedRobots.current[userId]) {
            if (selectedRobots[userId]) {
              await mx.invite(sroomid, userId);
            } else {
              // let AI leave room.
              sendAILeaveRoomCmd(userId);
            }
        }
      }
      // Update ref to current state after processing
      initialSelectedRobots.current = { ...selectedRobots };
    }
    setMenuAnchor(undefined);
  };

  useEffect(() => {
    const room = mx.getRoom(sroomid);
    const initialSelected: { [userId: string]: boolean } = {};
    if (room && aiAgents.length > 0) {
      aiAgents.forEach(agent => {
        const userId = `@${agent.UserName}:CurveServer.net`;
        if (room.currentState.members.hasOwnProperty(userId)) {
          const { members } = room.currentState;
          if (members[userId].membership === 'join') initialSelected[userId] = true;
          else initialSelected[userId] = false;
        } else {
          initialSelected[userId] = false;
        }
      });
    }
    setSelectedRobots(initialSelected);
    initialSelectedRobots.current = { ...initialSelected };
  }, [sroomid, mx, aiAgents]);

  const toggleRobot = (userId: string) => {
    setSelectedRobots(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getRobotLabel = (userId: string) => {
    const agent = aiAgents.find(a => `@${a.UserName}:CurveServer.net` === userId);
    return agent ? agent.DisplayName : userId;
  }

  return (
    <Box>
      <PopOut
        anchor={menuAnchor}
        offset={24}
        alignOffset={menuAnchor?.width === 0 ? 0 : 0}
        position="Right"
        align="Start"
        content={
          <FocusTrap
            focusTrapOptions={{
              initialFocus: false,
              returnFocusOnDeactivate: false,
              onDeactivate: () => setMenuAnchor(undefined),
              clickOutsideDeactivates: true,
              isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
              isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
              escapeDeactivates: undefined,
            }}
          >
            <Box direction="Column" grow="Yes" gap="200" className={css.InvitedSelection}>
              {aiAgents.map(agent => {
                const userId = `@${agent.UserName}:CurveServer.net`;
                return (
                  <div key={userId} style={{ display: 'flex' }}>
                    <Checkbox variant="positive" onToggle={() => toggleRobot(userId)} isActive={selectedRobots[userId]} />
                    <Text size="T300" style={{ paddingLeft: '10px' }}>{getRobotLabel(userId)}</Text>
                  </div>
                );
              })}
              <div style={{ marginLeft: 'auto', marginTop: 'auto' }}>
                <Button onClick={handleAddToRoom}>加入</Button>
              </div>
            </Box>
          </FocusTrap>
        }
      >
        <IconButton
          onClick={sroomid && handleOpenMenu}
          aria-pressed={!!menuAnchor}
          variant="Background"
          fill="None"
          size="300"
          radii="300"
        >
          <img src={robotLogo} style={{ width: '40px' }} alt='robotLogo' />
        </IconButton>
      </PopOut>
    </Box>
  );
}
/*
export function InvitedSelectionHuman( {sroomid}: InvitedSelectionProps ) {

    const mx = useMatrixClient();
    const [menuAnchor, setMenuAnchor] = useState<RectCords>();
    const [CustomerService1, setCustomerService1] = useState(false);

    const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
        setMenuAnchor(evt.currentTarget.getBoundingClientRect());
    };


    const sendAILeaveRoomCmd = useCallback(() => {
      if (sroomid) {
        const room = mx.getRoom(sroomid);
        const plainText = CurveMakeUIConfigCmd(JSON.stringify({cmd: actStrLeaveRoom, roomid: sroomid, userid: CurveCS1ID }));
        const msgType = MsgType.Text;

        const content: IContent = {
          msgtype: msgType,
          body: plainText,
        };
        if (room) {
          mx.sendMessage(room.roomId, content);
          // sendTypingStatus(false);
        }
      }
    }, [mx, sroomid]);

    const handleAddToRoom = async () => {
      if (sroomid) {
        if (CustomerService1) {
          const room = mx.getRoom(sroomid);
          // if (room && !room.currentState.members.hasOwnProperty(CurveAIID)) {
            await mx.invite(sroomid, CurveCS1ID);
          // }
        } else {
          // let AI leave room.
          sendAILeaveRoomCmd(); 
        } 
      }
      setMenuAnchor(undefined);
    };

    useEffect(() => {
      const room = mx.getRoom(sroomid)
      if (room && room.currentState.members.hasOwnProperty(CurveCS1ID)) {
        const {members} = room.currentState;
        if (members[CurveCS1ID].membership === 'join') setCustomerService1(true);
      } 
    }, [sroomid, mx]);

    return (
            <Box>
                   <PopOut
                    anchor={menuAnchor}
                    offset={24}
                    alignOffset={menuAnchor?.width === 0 ? 0 : 0}
                    position="Right"
                    align="Start"
                      content={
                        <FocusTrap
                          focusTrapOptions={{
                            initialFocus: false,
                            returnFocusOnDeactivate: false,
                            onDeactivate: () => setMenuAnchor(undefined),
                            clickOutsideDeactivates: true,
                            isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
                            isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
                            escapeDeactivates: undefined,
                          }}
                        >
                          <Box direction="Column" grow="Yes" gap="200" className={css.InvitedSelection}>
                            <div style={{display:'flex'}}>
                              <Checkbox variant="positive" onToggle={() => setCustomerService1(!CustomerService1)} isActive={CustomerService1} />
                              <Text size="T300" style={{paddingLeft:'10px'}}>客服1</Text>
                            </div>
                            <div style={{display:'flex'}}>
                              <Checkbox variant="positive" disabled/>
                              <Text size="T300" style={{paddingLeft:'10px'}}>業務</Text>
                            </div>
                            <div style={{display:'flex'}}>
                              <Checkbox variant="positive" disabled />
                              <Text size="T300" style={{paddingLeft:'10px'}}>PM</Text>
                            </div>
                            <div style={{marginLeft: 'auto', marginTop:'auto'}}>
                              <Button onClick={handleAddToRoom}>加入</Button>
                            </div>
                          </Box>
                        </FocusTrap>
                      }
                    >
                      <IconButton
                        onClick={sroomid && handleOpenMenu}
                        aria-pressed={!!menuAnchor}
                        variant="Background"
                        fill="None"
                        size="300"
                        radii="300"
                      >
                        <img src={personLogo} style={{width: '40px'}} alt='personLogo' />
                      </IconButton>
                    </PopOut>
            </Box>
        );
}
*/
export function InvitedSelectionByRole({ sroomid, role, icon, label }: { sroomid: string | undefined, role: string, icon: string, label: string }) {
  const mx = useMatrixClient();
  const [menuAnchor, setMenuAnchor] = useState<RectCords>();
  const [roleMembers, setRoleMembers] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ [userId: string]: boolean }>({});
  const initialSelectedUsers = useRef<{ [userId: string]: boolean }>({});

  const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    // Re-sync initial state when opening menu
    const room = sroomid ? mx.getRoom(sroomid) : null;
    const currentSelected: { [userId: string]: boolean } = {};
    roleMembers.forEach((userId: string) => {
      if (room && room.currentState.members[userId]?.membership === 'join') {
        currentSelected[userId] = true;
      } else {
        currentSelected[userId] = false;
      }
    });
    setSelectedUsers(currentSelected);
    initialSelectedUsers.current = { ...currentSelected };
    
    setMenuAnchor(evt.currentTarget.getBoundingClientRect());
  };

  // 發送離開指令
  const sendLeaveRoomCmd = useCallback((userId: string) => {
    if (!sroomid) return;
    const room = mx.getRoom(sroomid);
    const plainText = CurveMakeUIConfigCmd(JSON.stringify({
      cmd: actStrLeaveRoom,
      roomid: sroomid,
      userid: userId
    }));
    // 型別修正: 直接用 { msgtype, body } 物件
    const content: any = {
      msgtype: MsgType.Text,
      body: plainText,
    };
    if (room) {
      mx.sendMessage(room.roomId, content);
    }
  }, [mx, sroomid]);

  // 按下 [加入] 按鈕
  const handleAddToRoom = async () => {
    if (!sroomid) return;
    for (const userId of Object.keys(selectedUsers)) {
      // Compare with initial state
      if (selectedUsers[userId] !== initialSelectedUsers.current[userId]) {
        if (selectedUsers[userId]) {
          await mx.invite(sroomid, userId);
        } else {
          sendLeaveRoomCmd(userId);
        }
      }
    }
    // Update ref to current state after processing
    initialSelectedUsers.current = { ...selectedUsers };
    setMenuAnchor(undefined);
  };

  // 取得角色使用者清單
  useEffect(() => {
    const fetch = async () => {
      try {
        const userId = mx.getUserId();
        if (!userId) {
          // eslint-disable-next-line no-console
          console.error('Failed to get user ID');
          return;
        }

        let project: string | undefined;
        try {
          const profile = await mx.getProfileInfo(userId);
          if (profile && typeof profile.project === 'string') {
            project = profile.project;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to get profile info:', error);
        }

        if (!project) {
          // eslint-disable-next-line no-console
          console.error('Project not found in profile, cannot fetch users by role.');
          return;
        }
        console.log('project:', project);
        const users: string[] = await fetchUsersByRoles(mx, role, project);
        setRoleMembers(users);
        const initialSelected: { [userId: string]: boolean } = {};
        const room = sroomid ? mx.getRoom(sroomid) : null;
        users.forEach((userId: string) => {
          if (room && room.currentState.members[userId]?.membership === 'join') {
            initialSelected[userId] = true;
          } else {
            initialSelected[userId] = false;
          }
        });
        setSelectedUsers(initialSelected);
        initialSelectedUsers.current = { ...initialSelected };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch users by role:", err);
      }
    };
    if (sroomid) fetch();
  }, [mx, sroomid, role]);

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  return (
    <Box>
      <PopOut
        anchor={menuAnchor}
        offset={24}
        alignOffset={0}
        position="Right"
        align="Start"
        content={
          <FocusTrap
            focusTrapOptions={{
              initialFocus: false,
              returnFocusOnDeactivate: false,
              onDeactivate: () => setMenuAnchor(undefined),
              clickOutsideDeactivates: true,
              isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
              isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
              escapeDeactivates: undefined,
            }}
          >
            <Box direction="Column" gap="200" className={css.InvitedSelection} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <Text size="T300" style={{ fontWeight: 600, marginBottom: 8 }}>{label}</Text>
              {aiAgents.map(agent => {
                const userId = `@${agent.UserName}:CurveServer.net`;
                return (
                  <div key={userId} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      variant="positive"
                      onToggle={() => toggleRobot(userId)}
                      isActive={selectedRobots[userId]}
                    />
                    <Text size="T300" style={{ paddingLeft: '10px' }}>{getRobotLabel(userId)}</Text>
                  </div>
                );
              })}

              <div style={{ marginLeft: 'auto', marginTop: 'auto' }}>
                <Button onClick={handleAddToRoom}>加入</Button>
              </div>
            </Box>
          </FocusTrap>
        }
      >
        <IconButton
          onClick={sroomid ? handleOpenMenu : undefined}
          aria-pressed={!!menuAnchor}
          variant="Background"
          fill="None"
          size="300"
          radii="300"
        >
          <img src={icon} style={{ width: '40px' }} alt={label} />
        </IconButton>
      </PopOut>
    </Box>
  );
}

